'use client';

import { useEffect } from 'react';
import { User } from 'firebase/auth';
import { Session } from 'next-auth';

// Define an interface for the expected user structure within the NextAuth session
interface SessionUser {
  id?: string;
  role?: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
}

export interface UseRoleDeterminationParams {
  loading: boolean;
  user: User | null;
  userRole: string | null;
  setUserRole: (role: string | null) => void;
  setStudentSession: (session: any | null) => void;
  setCurrentViewRole: (role: 'parent' | 'operator' | null) => void;
  status: string;
  session: Session | null;
}

/**
 * Owns the role-syncing effect for the AuthProvider.
 * Extracted verbatim from AuthProvider.tsx — no behavioral changes.
 */
export function useRoleDetermination({
  loading,
  user,
  userRole,
  setUserRole,
  setStudentSession,
  setCurrentViewRole,
  status,
  session,
}: UseRoleDeterminationParams): void {
  useEffect(() => {
    // Skip if still loading auth or session
    if (loading || status === 'loading') return;

    // Handle user authentication state for both Firebase and NextAuth
    const sessionUser = session?.user as SessionUser | undefined;
    const isAuthenticated = (!!user && !!user.uid) || status === 'authenticated';

    if (isAuthenticated) {
      // If user is authenticated but no role is set, attempt to determine role
      if (!userRole) {
        console.log("AuthProvider: User authenticated but no role set. Attempting to determine role...");

        // First check NextAuth session for role (for parents/operators)
        if (status === 'authenticated' && sessionUser?.role) {
          console.log(`AuthProvider: Found role in NextAuth session: ${sessionUser.role}`);

          // For NextAuth users, get comprehensive role information from API
          const fetchUserRole = async () => {
            try {
              console.log('AuthProvider: Attempting to fetch user role from API...');
              const response = await fetch('/api/auth/get-user-role', {
                method: 'GET',
                credentials: 'include', // Include cookies for NextAuth session
                headers: {
                  'Content-Type': 'application/json',
                }
              });

              console.log('AuthProvider: API response status:', response.status);

              if (response.ok) {
                const roleData = await response.json();
                if (roleData.success) {
                  console.log(`AuthProvider: API returned role: ${roleData.role}`, roleData);
                  setUserRole(roleData.role);
                  localStorage.setItem('user_role', roleData.role);

                  // Set current view role based on the comprehensive role
                  if (roleData.role === 'both') {
                    // For dual-role users, check if there's a preferred view stored or default to parent
                    const storedViewRole = localStorage.getItem('current_view_role') as 'parent' | 'operator' | null;
                    const preferredView = storedViewRole || 'parent';
                    setCurrentViewRole(preferredView);
                    localStorage.setItem('current_view_role', preferredView);
                    console.log(`AuthProvider: Set view role to ${preferredView} for dual-role user`);
                  } else if (roleData.role === 'parent' || roleData.role === 'operator') {
                    setCurrentViewRole(roleData.role as 'parent' | 'operator');
                    localStorage.setItem('current_view_role', roleData.role);
                  }
                  return;
                } else {
                  console.log('AuthProvider: API returned unsuccessful response:', roleData);
                }
              } else {
                console.log('AuthProvider: API returned error status:', response.status);
              }

              // Fallback to session role if API fails
              console.log('AuthProvider: API failed, using session role as fallback');
              if (sessionUser?.role) {
                setUserRole(sessionUser.role);
                localStorage.setItem('user_role', sessionUser.role);

                // Set view role for session fallback
                if (sessionUser.role === 'both') {
                  const storedViewRole = localStorage.getItem('current_view_role') as 'parent' | 'operator' | null;
                  const preferredView = storedViewRole || 'parent';
                  setCurrentViewRole(preferredView);
                  localStorage.setItem('current_view_role', preferredView);
                } else if (sessionUser.role === 'parent' || sessionUser.role === 'operator') {
                  setCurrentViewRole(sessionUser.role as 'parent' | 'operator');
                  localStorage.setItem('current_view_role', sessionUser.role);
                }
              }
            } catch (error) {
              console.error('AuthProvider: Error fetching user role:', error);
              // Fallback to session role on network error
              if (sessionUser?.role) {
                console.log('AuthProvider: Using session role as fallback due to error');
                setUserRole(sessionUser.role);
                localStorage.setItem('user_role', sessionUser.role);

                // Set view role for error fallback
                if (sessionUser.role === 'both') {
                  const storedViewRole = localStorage.getItem('current_view_role') as 'parent' | 'operator' | null;
                  const preferredView = storedViewRole || 'parent';
                  setCurrentViewRole(preferredView);
                  localStorage.setItem('current_view_role', preferredView);
                } else if (sessionUser.role === 'parent' || sessionUser.role === 'operator') {
                  setCurrentViewRole(sessionUser.role as 'parent' | 'operator');
                  localStorage.setItem('current_view_role', sessionUser.role);
                }
              }
            }
          };

          fetchUserRole();
          return;
        }

        // Check for role in localStorage
        const storedRole = localStorage.getItem('user_role');

        if (storedRole) {
          console.log(`AuthProvider: Found role in localStorage: ${storedRole}`);
          setUserRole(storedRole);

          // If this is a student, check for student ID
          if (storedRole === 'student') {
            const storedStudentId = localStorage.getItem('student_id') || localStorage.getItem('current_session');
            if (storedStudentId) {
              console.log(`AuthProvider: Found student ID in localStorage: ${storedStudentId}`);
              setStudentSession(storedStudentId);
            }
          }
        } else if (user) {
          // Firebase user specific logic
          // Check for parent indicators
          const isParent = localStorage.getItem('parent_id') || localStorage.getItem('parent_name');

          // Check for temporary student session (parent viewing student dashboard)
          const tempStudentSession = localStorage.getItem('temp_student_session');

          if (isParent) {
            console.log("AuthProvider: User appears to be a parent based on localStorage");
            setUserRole('parent');

            // If parent is viewing a student dashboard, set the student session
            if (tempStudentSession) {
              console.log(`AuthProvider: Parent viewing student dashboard for: ${tempStudentSession}`);
              setStudentSession(tempStudentSession);
            }
          } else {
            // Default to student role if no other indicators
            console.log("AuthProvider: No role indicators found, defaulting to student");
            setUserRole('student');

            // Try to find student ID
            const storedStudentId = localStorage.getItem('student_id') || localStorage.getItem('current_session');
            if (storedStudentId) {
              console.log(`AuthProvider: Found student ID: ${storedStudentId}`);
              setStudentSession(storedStudentId);
            } else {
              console.log(`AuthProvider: No student ID found, using UID: ${user.uid}`);
              setStudentSession(user.uid);
              localStorage.setItem('student_id', user.uid);
              localStorage.setItem('current_session', user.uid);
            }
          }
        }
      }
      // End of role determination logic
    }
  }, [loading, user, userRole, setUserRole, setStudentSession, status, session]);
}
